if (!customElements.get("sqft-calculator")) {
  class SquareFootSelector extends HTMLElement {
    constructor() {
      super();

      // Data caching
      const optionValuesTextContent =
        document.querySelector("#option_values")?.textContent;
      this.optionValues = JSON.parse(optionValuesTextContent || "[]");
      let selectedTexture = this.querySelector('input[name="product-texture"]:checked');

      // DOM Selectors
      this.elements = {
        drawer: this.querySelector(".sqft_drawer"),
        closeBtn: this.querySelector(".sqft_drawer_close"),
        overlay: this.querySelector(".sqft_drawer_overlay"),
        heightInput: this.querySelector("#height"),
        widthInput: this.querySelector("#width"),
        unitRadios: this.querySelectorAll('input[name="unit"]'),
        errorMessage: this.querySelector(".error-message"),
        sqftDisplay: this.querySelector(".sqft-value"),
        sqftDisplay2: this.querySelector(".sqft-card-header-sqft"),
        priceDisplay: this.querySelector(".sqft-product-price"),
        quantityDisplay: this.querySelector("#product_quantity"),
        decreaseBtn: this.querySelector("#sqft-quantity-decrease-btn"),
        increaseBtn: this.querySelector("#sqft-quantity-increase-btn"),
        addToCartBtn: this.querySelector("#sqft-add-to-cart-btn"),
        customSizeBtn: document.querySelector("#select_custom_size_btn"),
        variantIdInput: document.querySelector(".product-variant-id"),
        textureRadios: this.querySelectorAll('input[name="product-texture"]'),
        warningsMsg: this.querySelector(".sqft-card-warning"),
        sqftTextCard: this.querySelector('.sqft-card-header'),
        isEmbrWallArtCustom: this.dataset.isEmbrWallArtCustom,
      };

      this.elements.customSizeBtnText = this.elements.customSizeBtn?.textContent || '';

      // State attributes
      this.state = {
        currentUnit: "in",
        height: 0,
        width: 0,
        quantity: 1,
        baseSqft: 0,
        basePrice: 0,
        variantId: null,
        currentClosestOptionId: null,
        texture: selectedTexture?.value
      };

      this.conversionToFeet = {
        in: 1 / 12,
        mm: 0.0328084 / 10, // mm to feet (approx) or use a more precise one
      };

      // Update mm constant to be more accurate (1mm = 0.00328084 ft)
      this.conversionToFeet.mm = 0.00328084;

      this.storageKey = "sqft-calculator-state";
      this.debouncedRenderPrice = debounce(this.renderPrice.bind(this), 500);
    }

    connectedCallback() {
      this.loadState();
      this.initEventListeners();
      this.calculate();
    }

    initEventListeners() {
      // Input handling
      const allowOnlyIntegers = (e) => {
        // Allow control keys
        const allowedKeys = [
          "Backspace",
          "Delete",
          "ArrowLeft",
          "ArrowRight",
          "Tab",
        ];

        if (allowedKeys.includes(e.key)) return;

        // Block decimals, minus, e, etc.
        if (!/^[0-9]$/.test(e.key)) {
          e.preventDefault();
        }
      };

      const handleInput = () => {
        this.calculate();
        this.changeBtnText();
      };

      this.elements.heightInput.addEventListener("keydown", allowOnlyIntegers);
      this.elements.widthInput.addEventListener("keydown", allowOnlyIntegers);
      this.elements.heightInput.addEventListener("input", handleInput);
      this.elements.widthInput.addEventListener("input", handleInput);

      this.elements.unitRadios.forEach((radio) => {
        radio.addEventListener("change", (e) => {
          this.state.currentUnit = e.target.value;
          this.calculate();
        });
      });

      if (this.elements.textureRadios) {
        this.elements.textureRadios.forEach((texture) => {
          texture.addEventListener("change", (e) => {
            this.state.texture = e.target.value;
          });
        });
      }

      // Quantity
      this.elements.decreaseBtn.addEventListener("click", () =>
        this.updateQuantity(-1)
      );
      this.elements.increaseBtn.addEventListener("click", () =>
        this.updateQuantity(1)
      );

      // Drawer
      this.elements.customSizeBtn?.addEventListener("click", () =>
        this.toggleDrawer(true)
      );
      this.elements.closeBtn.addEventListener("click", () =>
        this.toggleDrawer(false)
      );
      this.elements.overlay.addEventListener("click", () =>
        this.toggleDrawer(false)
      );

      // Prevent theme re-render conflict
      this.addEventListener("change", (e) => e.stopPropagation());

      // Cart
      this.elements.addToCartBtn.addEventListener("click", () =>
        this.addToCart()
      );

      // Listen for variant changes to update variant ID when color/material changes
      subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, () => {
        if (this.state.currentClosestOptionId && this.debouncedRenderPrice) {
          this.updateSelectedOptionValues();
          this.debouncedRenderPrice();
        }
      });
    }

    updateQuantity(delta) {
      const newQty = this.state.quantity + delta;
      if (newQty >= 1) {
        this.state.quantity = newQty;
        this.renderUI();
        this.saveState();
      }
    }

    /** For embroidered wall art: set max on the other dimension when one > 57 (don't change values). */
    applyEmbroideredWallArtDimensionMax() {
      if (this.elements.isEmbrWallArtCustom !== 'true') return;
      const MAX_ONE_DIM = 57;
      const h = parseInt(this.elements.heightInput.value, 10) || 0;
      const w = parseInt(this.elements.widthInput.value, 10) || 0;

      if (w > MAX_ONE_DIM) {
        this.elements.heightInput.setAttribute('max', String(MAX_ONE_DIM));
      } else {
        this.elements.heightInput.removeAttribute('max');
      }
      if (h > MAX_ONE_DIM) {
        this.elements.widthInput.setAttribute('max', String(MAX_ONE_DIM));
      } else {
        this.elements.widthInput.removeAttribute('max');
      }
    }

    /** Returns true if embroidered wall art dimension rule is violated (both > 57). */
    hasEmbroideredWallArtDimensionError() {
      if (this.elements.isEmbrWallArtCustom !== 'true') return false;
      const MAX_ONE_DIM = 57;
      const h = parseInt(this.elements.heightInput.value, 10) || 0;
      const w = parseInt(this.elements.widthInput.value, 10) || 0;
      return h > MAX_ONE_DIM && w > MAX_ONE_DIM;
    }

    calculate() {
      let h = parseInt(this.elements.heightInput.value, 10) || 0;
      let w = parseInt(this.elements.widthInput.value, 10) || 0;

      if (!h || !w) {
        this.resetUI();
        return;
      }

      // Embroidered wall art: set max on other field when one > 57 (no value reset)
      this.applyEmbroideredWallArtDimensionMax();

      // Embroidered wall art: show error if both > 57, don't reset values
      if (this.hasEmbroideredWallArtDimensionError()) {
        this.setError('Only one dimension can exceed 57. Please set height or width to 57 or less.');
        return;
      }

      const areaInSqFeet =
        h *
        this.conversionToFeet[this.state.currentUnit] *
        (w * this.conversionToFeet[this.state.currentUnit]);

      let calculatedArea;
      let baseSqft;
      // Logic: 10% wastage rounded down to nearest 5


      if (this.elements.isEmbrWallArtCustom === 'true') {
        calculatedArea = areaInSqFeet;
        baseSqft = Math.ceil(calculatedArea)
      } else {
        calculatedArea = areaInSqFeet * 1.1;
        baseSqft = Math.floor(calculatedArea / 5) * 5;
      }

      const min = +this.optionValues[0]?.value || 0;

      if (baseSqft < min) {
        baseSqft = calculatedArea;
        this.elements.warningsMsg.style.display = "block";
      } else {
        this.elements.warningsMsg.style.display = "none";
      }


      this.state.baseSqft = baseSqft;

      this.validateAndMatchOption();
      this.renderUI();
      this.saveState();
    }

    validateAndMatchOption() {
      const firstOption = this.optionValues[0];
      const min = +firstOption?.value || 0;
      const max =
        +this.optionValues[this.optionValues.length - 1]?.value || Infinity;

      if (this.state.baseSqft > max) {
        this.setError(`Area is too large. Maximum ${max} sq ft exceeded.`);
        // this.showRequestChat(true);
      } else {
        this.setError(null);
        //  this.showRequestChat(false);
        let match;
        if (this.state.baseSqft < min) {
          match = firstOption;
        } else {
          match = this.optionValues.find(
            (o) => +o.value === this.state.baseSqft
          );
        }
        if (match) {
          if (this.state.currentClosestOptionId === match.id) return;

          this.state.currentClosestOptionId = match.id;
          this.updateSelectedOptionValues();
          this.debouncedRenderPrice();
        }
      }
    }

    getAllSelectedOptionValues() {
      // Get all currently selected option values from variant picker (color, material, etc.)
      const variantSelects = document.querySelector('product-variant-selects');
      if (!variantSelects) return [];

      // Use the same method as ProductVariantSelects to get selected option values
      const selectedOptions = Array.from(
        variantSelects.querySelectorAll("select option[selected], fieldset input:checked")
      ).map(({ dataset }) => dataset.optionValueId).filter(Boolean);

      return selectedOptions;
    }

    updateSelectedOptionValues() {
      // Get fresh color/material options from DOM
      const allSelectedOptions = this.getAllSelectedOptionValues();

      // Combine with Sq. Ft option ID if available
      if (this.state.currentClosestOptionId) {
        this.selectedOptionValues = [...allSelectedOptions, this.state.currentClosestOptionId];
      } else {
        this.selectedOptionValues = allSelectedOptions;
      }
    }

    async renderPrice() {
      const sectionId = 'data-selected-variant';
      // Always update selectedOptionValues with fresh values before fetching variant
      this.updateSelectedOptionValues();

      // Ensure we have at least one option value
      if (!this.selectedOptionValues || this.selectedOptionValues.length === 0) {
        console.warn("No option values selected");
        return;
      }

      try {
        const url = `${this.dataset.productUrl}?section_id=${sectionId}&option_values=${this.selectedOptionValues.join(",")}`;
        const response = await fetch(url);
        const htmlText = await response.text();
        const html = new DOMParser().parseFromString(htmlText, "text/html");
        const data = JSON.parse(
          html.querySelector("[selected-variant]")?.textContent || "{}"
        );

        this.state.variantId = data.id;
        this.state.basePrice = (data.price || 0) / 100;

        this.renderUI();
        this.updateGlobalVariant(data.id);
      } catch (err) {
        console.error("Price fetch failed:", err);
      }
    }

    updateGlobalVariant(id) {
      if (!id) return;
      if (this.elements.variantIdInput) this.elements.variantIdInput.value = id;

      const url = new URL(window.location.href);
      url.searchParams.set("variant", id);
      window.history.replaceState({ path: url.toString() }, "", url.toString());
    }

    setError(msg) {
      const errorTextEl = this.elements.errorMessage.querySelector('.product_error-message');
      errorTextEl.textContent = msg;
      this.elements.errorMessage.style.display = msg ? "flex" : "none";
      this.elements.addToCartBtn.disabled = !!msg;
    }

    // showRequestChat(value){
    //   this.elements.showChatRequest.style.display = value ? "flex" : "none";
    // }

    resetUI() {
      this.elements.sqftDisplay.textContent = "0 sq. ft.";
      this.elements.sqftDisplay2.textContent = "0 sq. ft.";
      this.elements.priceDisplay.innerHTML = "<sup>$</sup>0";
      this.setError(null);
      // this.showRequestChat(false);
      this.elements.addToCartBtn.disabled = true;
      // this.elements.sqftTextCard.style.display='none';
      this.saveState();
    }

    renderUI() {
      const totalSqft = parseFloat(
        (this.state.baseSqft * this.state.quantity).toFixed(2)
      );
      this.elements.sqftDisplay.textContent = `${totalSqft} sq. ft.`;
      this.elements.sqftDisplay2.textContent = `${totalSqft} sq. ft.`;
      this.elements.quantityDisplay.textContent = this.state.quantity;

      if (this.state.basePrice) {
        const total = this.state.basePrice * this.state.quantity;
        this.elements.priceDisplay.innerHTML = `<sup>$</sup>${total.toLocaleString(
          "en-IN"
        )}`;
      }
    }


    changeBtnText() {
      const hasSize = this.elements.heightInput.value !== "" || this.elements.widthInput.value !== "";

      const hasBothSize = this.elements.heightInput.value !== ""
        && this.elements.widthInput.value !== ""
        && this.elements.heightInput.value > 0
        && this.elements.widthInput.value > 0;

      if (hasSize) {
        const originalText = this.elements.customSizeBtnText.trim();
        const updatedText = originalText.replace(/^\S+/, 'Edit');
        this.elements.customSizeBtn.textContent = updatedText;
      } else {
        this.elements.customSizeBtn.textContent = this.elements.customSizeBtnText;
      }

      // this.elements.customSizeBtn.textContent = hasSize ? "Edit Size" : this.elements.customSizeBtnText;
      this.elements.sqftTextCard.style.display = hasBothSize ? 'block' : 'none';
    }


    async addToCart() {
      if (!this.state.variantId)
        return this.setError("Invalid size selection.");
      // const selectedTextureEl = this.querySelector(
      //   'input[name="product-texture"]:checked'
      // );

      const body = JSON.stringify({
        items: [
          {
            id: this.state.variantId,
            quantity: this.state.quantity,
            properties: {
              Height: `${this.elements.heightInput.value} ${this.state.currentUnit}`,
              Width: `${this.elements.widthInput.value} ${this.state.currentUnit}`,
              Area: `${parseFloat(this.state.baseSqft.toFixed(2))} Sq. Ft.`,
              Texture: this.state.texture,
              // "Total Area": `${parseFloat(
              //   (this.state.baseSqft * this.state.quantity).toFixed(2)
              // )} Sq. Ft.`,
            },
          },
        ],
        sections: ["cart-drawer"],
      });

      try {
        this.elements.addToCartBtn.classList.add("is-loading");
        const response = await fetch(
          `${window.Shopify.routes.root}cart/add.js`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body,
          }
        );
        const data = await response.json();
        const cart =
          document.querySelector("cart-notification") ||
          document.querySelector("cart-drawer");

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: "sqft-calculator",
          productVariantId: this.state.variantId,
          cartData: data,
        });

        this.toggleDrawer(false);
        setTimeout(() => {
          if (cart?.renderContents) cart.renderContents(data);
        }, 200);
      } catch (err) {
        console.error("Cart add failed:", err);
      } finally {
        this.elements.addToCartBtn.classList.remove("is-loading");
      }
    }

    toggleDrawer(active) {
      const overflow = active ? "hidden" : "auto";
      document.body.style.overflow = overflow;

      const zIndex = active ? "auto" : "";

      if (window.innerWidth > 750) {
        const header = document.querySelector(".section-header");
        const announce = document.querySelector(".section-announcement-bar");

        if (header) header.style.zIndex = zIndex;
        if (announce) announce.style.zIndex = zIndex;
      }

      this.elements.drawer.classList.toggle("is--active", active);
      this.elements.overlay.classList.toggle("is--active", active);
    }

    saveState() {
      // const payload = {
      //   unit: this.state.currentUnit,
      //   height: this.elements.heightInput.value,
      //   width: this.elements.widthInput.value,
      //   quantity: this.state.quantity,
      // };
      const productHandle = this.dataset.productHandle;

      const payload = {
        unit: this.state.currentUnit,
        height: this.elements.heightInput.value,
        width: this.elements.widthInput.value,
        quantity: this.state.quantity,
      };
      const storedData = JSON.parse(localStorage.getItem(this.storageKey)) || {};
      if (!Array.isArray(storedData[productHandle])) {
        storedData[productHandle] = [];
      }

      storedData[productHandle][0] = payload;
      // console.log('storedData',storedData)
      localStorage.setItem(this.storageKey, JSON.stringify(storedData));
    }

    loadState() {
      try {
        const saved = JSON.parse(localStorage.getItem(this.storageKey));
        if (!saved) return;

        const productHandle = this.dataset.productHandle;
        const savedArray = saved[productHandle];

        const savedData = savedArray[0];

        this.state.currentUnit = savedData.unit || "in";
        this.state.quantity = savedData.quantity || 1;
        this.elements.heightInput.value = savedData.height || "";
        this.elements.widthInput.value = savedData.width || "";

        const radio = this.querySelector(
          `input[name="unit"][value="${this.state.currentUnit}"]`
        );
        if (radio) radio.checked = true;

        this.changeBtnText();

      } catch (e) {
        console.warn("Storage load failed", e);
      }
    }
  }

  customElements.define("sqft-calculator", SquareFootSelector);
}